/* React */
import React, { useState, useEffect } from "react";

/* Utils */
import SwiperWrapper from "src/utils/wrapper/swiper-wrapper/SwiperWrapper";
import { downloadTableAsCsv } from "src/utils/helper/charts/downloadTableAsCsv";

/* Components */
import TableOptions from "src/components/shared/table/table-options/TableOptions";
import TableSlide from "src/components/shared/table/table-slide/TableSlide";
import Pagination from "src/components/shared/swiper/swiper-controls/pagination/Pagination";

/* Constants */
import { ChartType } from "src/shared/constants/charts/Chart";

/* Stylesheet */
import "swiper/css";
import "swiper/css/pagination";
import styles from "./Table.module.scss";

/* Interfaces */
import { TableProps } from "src/shared/interfaces/charts/Table";
import { getColorByIndex } from "src/utils/helper/charts/colorMapper";

const COLUMNS_PER_PAGE = 5;

const Table: React.FC<{
  index: number;
  stacked: boolean;
  label?: string;
  data: TableProps[];
  labelMapping?: Record<string, string>;
  downloadFilename?: string;
  splitCountPercent?: boolean;
}> = ({ stacked, label, data, labelMapping, downloadFilename, splitCountPercent }) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [showTable, setShowTable] = useState<boolean>(true);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  // Ref-based guard flips synchronously so a second click that arrives
  // before React commits the isDownloading state update is still blocked.
  const isDownloadingRef = React.useRef(false);

  const toggleShowTable = () => {
    setShowTable((showTable: boolean) => !showTable);
  };

  const handleDownload = () => {
    if (isDownloadingRef.current) return;
    isDownloadingRef.current = true;
    setIsDownloading(true);
    try {
      downloadTableAsCsv(
        data,
        downloadFilename ?? label ?? "table",
        labelMapping,
        splitCountPercent
      );
    } finally {
      // Defer so the button stays disabled until the deferred cleanup in
      // downloadTableAsCsv (setTimeout 0) has at least been queued, which
      // prevents repeated rapid clicks from triggering multiple downloads.
      window.setTimeout(() => {
        isDownloadingRef.current = false;
        setIsDownloading(false);
      }, 0);
    }
  };

  const startIndex = currentPage * COLUMNS_PER_PAGE;
  const endIndex = startIndex + COLUMNS_PER_PAGE;

  useEffect(() => {
    // Set currentPage to 0 when activeIndex changes
    setCurrentPage(0);
  }, [activeIndex]);

  const handleActiveIndex = (index: number) => {
    setActiveIndex(index);
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (
      currentPage <
      Math.ceil(
        data[activeIndex].data.length / COLUMNS_PER_PAGE,
      ) -
        1
    ) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (
    !data ||
    data.length === 0 ||
    data.every((d) => !d.data || d.data.length === 0)
  ) {
    return null;
  }

  const rowLength: number = Object.keys(
    data[0].data[0],
  ).length;

  const renderPagination = () => {
    return (
      <Pagination
        disabled={{
          prevDisabled: currentPage === 0,
          nextDisabled:
            currentPage ===
            Math.ceil(
              data[activeIndex].data.length /
                COLUMNS_PER_PAGE,
            ) -
              1,
        }}
        currentPage={currentPage}
        handlePrevPage={handlePrevPage}
        handleNextPage={handleNextPage}
      />
    );
  };

  return (
    <div className={styles.table}>
      <TableOptions
        label={label}
        showTable={showTable}
        toggleShowTable={toggleShowTable}
        onDownload={downloadFilename !== undefined ? handleDownload : undefined}
        isDownloading={isDownloading}
      />
      {showTable ? (
        <SwiperWrapper
          scssProps={{
            backgroundColor:
              rowLength % 2 === 0
                ? "#ffffff"
                : "var(--ion-gradient-light)",
          }}
          label={data[activeIndex].name}
          data={data}
          pagination={renderPagination()}
          handleActiveIndex={handleActiveIndex}
          options={{ spaceBetween: 8 }}
          renderItem={(item, index) => {
            const baseColor = getColorByIndex(index);

            return (
              <TableSlide
                key={index}
                data={item.data}
                startIndex={startIndex}
                endIndex={endIndex}
                labelMapping={labelMapping}
                color={stacked ? baseColor : undefined}
              />
            );
          }}
        />
      ) : undefined}
    </div>
  );
};

export default Table;
